/**
 * Phase 99a (10/07/2026) — Widget « Réassort MOMA-calc ».
 *
 * Calcule le résiduel théorique par slot = dernier chargement AM − ventes MOMA
 * depuis. Compare avec le stock AM lots pour révéler les écarts (potentiellement
 * causés par des « ruptures de contrat prestataire » — partenaires qui prennent
 * du stock sans le déclarer).
 *
 * Affichage :
 *   - Tableau par slot : Slot · Produit · Cap · Chargé · Ventes · MOMA-calc · AM lots · Écart · À recharger
 *   - Coloration écart : > 0 rouge (perte suspecte), < 0 jaune (sync retard), = 0 vert
 *   - Bouton « ⚡ Appliquer les écarts » (manuel, WASTE FIFO avec motif litige)
 *   - Toggle Settings « Auto-WASTE écarts » (défaut OFF, sécurité)
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Zap, TrendingDown, TrendingUp, Rocket, Search } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import QuickRefillDialog from "./QuickRefillDialog";
import SlotAuditDrawer from "./SlotAuditDrawer";

export default function MomaReassortWidget({ distributorId }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [report, setReport] = useState(null);
  const [autoWasteEnabled, setAutoWasteEnabled] = useState(false);
  const [togglingSetting, setTogglingSetting] = useState(false);
  const [quickRefillOpen, setQuickRefillOpen] = useState(false);
  // Phase 101dq — Panneau audit slot
  const [auditSlot, setAuditSlot] = useState(null);
  const confirm = useConfirm();

  const loadReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/distributors/${distributorId}/moma-reassort`);
      setReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement MOMA-calc");
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const { data } = await api.get(`/settings`);
      setAutoWasteEnabled(Boolean(data?.auto_waste_moma_discrepancy));
    } catch (e) {
      // silencieux — le settings endpoint est parfois protégé
    }
  };

  useEffect(() => {
    loadReport();
    loadSettings();
  }, [distributorId]);

  const toggleAutoWaste = async () => {
    const newValue = !autoWasteEnabled;
    const ok = await confirm({
      title: newValue ? "Activer l'auto-WASTE des écarts ?" : "Désactiver l'auto-WASTE ?",
      description: newValue
        ? "Le cron 15 min générera automatiquement un mouvement WASTE avec motif « Rupture de contrat prestataire » sur chaque écart positif (AM_lots > MOMA_calc). Utile pour tracer les partenaires soupçonnés."
        : "L'auto-WASTE sera désactivé. Vous pourrez toujours appliquer manuellement les écarts via le bouton.",
      confirmText: newValue ? "Activer" : "Désactiver",
      destructive: newValue,
    });
    if (!ok) return;
    setTogglingSetting(true);
    try {
      await api.put(`/settings`, { auto_waste_moma_discrepancy: newValue });
      setAutoWasteEnabled(newValue);
      toast.success(newValue ? "Auto-WASTE activé" : "Auto-WASTE désactivé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur mise à jour");
    } finally {
      setTogglingSetting(false);
    }
  };

  const applyWasteNow = async () => {
    const positives = (report?.slots || []).filter((s) => s.delta > 0.001);
    const totalQty = positives.reduce((sum, s) => sum + s.delta, 0);
    if (positives.length === 0) {
      toast.info("Aucun écart positif à traiter");
      return;
    }
    const ok = await confirm({
      title: `Appliquer WASTE sur ${positives.length} slot(s) ?`,
      description:
        `${totalQty.toFixed(2)} barquette(s) au total seront retirées des lots AM (FIFO) avec le motif « Rupture de contrat prestataire ». Cette action est irréversible.`,
      confirmText: "Appliquer maintenant",
      destructive: true,
    });
    if (!ok) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/distributors/${distributorId}/moma-reassort/apply-waste`);
      toast.success(`${data.applied_count} mouvement(s) WASTE créé(s)`);
      if (data.warnings?.length) {
        data.warnings.forEach((w) => toast.warning(w));
      }
      await loadReport();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur application WASTE");
    } finally {
      setApplying(false);
    }
  };

  if (loading && !report) {
    return (
      <div className="p-6 border border-[#E5E5E0] bg-white text-center text-[#575A57]" data-testid="moma-widget-loading">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Calcul du réassort MOMA…
      </div>
    );
  }

  if (!report) return null;

  const slots = report.slots || [];
  const totals = report.totals || {};
  const hasSlots = slots.length > 0;
  const positivesCount = totals.slots_with_positive_delta || 0;
  const negativesCount = totals.slots_with_negative_delta || 0;

  return (
    <div className="border border-[#E5E5E0] bg-white" data-testid="moma-widget">
      {/* Header widget */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E0] bg-[#FAFAF7] flex-wrap">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#1F4E3D]" />
          <h3 className="font-semibold text-[#1A1C1A]">Stock machine (Nayax) vs AM</h3>
          <Badge variant="outline" className="text-[10px] bg-white">
            {slots.length} slot(s)
          </Badge>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          {/* Toggle Auto-WASTE */}
          <label className="flex items-center gap-1.5 text-xs text-[#575A57] cursor-pointer" title="Cron 15 min crée automatiquement des WASTE sur les écarts positifs">
            <input
              type="checkbox"
              checked={autoWasteEnabled}
              onChange={toggleAutoWaste}
              disabled={togglingSetting}
              className="accent-[#C05C47]"
              data-testid="moma-auto-waste-toggle"
            />
            Auto-WASTE écarts
          </label>
          {positivesCount > 0 && (
            <Button
              size="sm"
              onClick={applyWasteNow}
              disabled={applying}
              className="bg-[#C05C47] hover:bg-[#A04E2C] h-8"
              data-testid="moma-apply-waste-btn"
              title={`Générer WASTE 'Rupture de contrat' sur les ${positivesCount} slot(s) en écart`}
            >
              {applying ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Zap className="w-3 h-3 mr-1" />
              )}
              Appliquer les écarts
            </Button>
          )}
          {/* Phase 99b — Refill éclair mobile */}
          <Button
            size="sm"
            onClick={() => setQuickRefillOpen(true)}
            className="bg-[#1F4E3D] hover:bg-[#173D2F] h-8"
            data-testid="moma-quick-refill-btn"
            title="Saisir un refill physique en 30 secondes — rafraîchit la baseline MOMA-calc"
          >
            <Rocket className="w-3 h-3 mr-1" />
            Refill éclair
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadReport}
            disabled={loading}
            className="h-8"
            data-testid="moma-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Totaux résumé — Phase 101dr : Nayax = vérité, AM = à corriger si divergent */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 px-4 py-3 border-b border-[#E5E5E0]">
        <StatCard label="Total Nayax" value={totals.total_moma} color="#8C3D31" />
        <StatCard label="Total AM lots" value={totals.total_am} color="#1A1C1A" />
        <StatCard
          label="Écart total (AM − Nayax)"
          value={totals.total_delta}
          color={totals.total_delta > 0 ? "#C05C47" : totals.total_delta < 0 ? "#8C6B2F" : "#1F4E3D"}
          warn={Math.abs(totals.total_delta) > 0.001}
          icon={totals.total_delta > 0 ? <TrendingDown className="w-3 h-3" /> : totals.total_delta < 0 ? <TrendingUp className="w-3 h-3" /> : null}
        />
        <StatCard
          label="Slots à recharger"
          value={totals.total_to_recharge}
          color="#1F4E3D"
        />
        <StatCard
          label="AM en excès"
          value={positivesCount}
          color="#C05C47"
          warn={positivesCount > 0}
          suffix={positivesCount > 0 ? "⚠️" : "✓"}
        />
      </div>

      {/* Empty state */}
      {!hasSlots && (
        <div className="p-6 text-center text-[#A3A6A3]">
          <p>Aucun slot configuré pour ce distributeur (pas de <code>displayed_product_rails</code>).</p>
        </div>
      )}

      {/* Phase 101dr — Bandeau Nayax indispo (mode dégradé) */}
      {report.nayax_error && (
        <div className="bg-[#FBEDEA] border-t border-b border-[#C05C47] px-4 py-2 text-xs text-[#8C3D31]">
          <span className="font-semibold">⚠ Nayax indisponible</span> — valeurs affichées basées sur la
          dernière baseline tournée (mode dégradé). Refresh dans quelques minutes.
        </div>
      )}

      {/* Tableau détaillé — Phase 101dr : 3 colonnes vérité (Nayax · AM · Δ) */}
      {hasSlots && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="moma-slots-table">
            <thead className="bg-[#FAFAF7]">
              <tr>
                <th className="text-left px-2 py-2 w-12">Slot</th>
                <th className="text-left px-2 py-2">Produit</th>
                <th className="text-center px-2 py-2 w-14">Cap</th>
                <th className="text-center px-2 py-2 w-20 bg-[#FBEDEA]" title="Nayax machineProducts = vérité machine">
                  Nayax live
                </th>
                <th className="text-center px-2 py-2 w-16" title="Somme des lots AM au slot">
                  AM lots
                </th>
                <th className="text-center px-2 py-2 w-16" title="AM − Nayax. Positif = AM en excès, à corriger ; négatif = AM en manque">
                  Δ
                </th>
                <th className="text-center px-2 py-2 w-20">À recharger</th>
                <th className="text-center px-2 py-2 w-10">Audit</th>
              </tr>
            </thead>
            <tbody>
              {slots.map((s) => {
                const deltaColor =
                  s.delta > 0.001
                    ? "bg-[#FBEDEA] text-[#C05C47] font-bold"
                    : s.delta < -0.001
                    ? "bg-[#FBF4E9] text-[#8C6B2F] font-semibold"
                    : "text-[#6B6E6B]";
                const isDegraded = s.qty_moma_source && s.qty_moma_source !== "nayax_live";
                return (
                  <tr
                    key={`${s.product_id}-${s.slot_number}`}
                    className="border-t border-[#E5E5E0] hover:bg-[#FAFAF7]"
                    data-testid={`moma-slot-row-${s.slot_number}`}
                  >
                    <td className="px-2 py-2 font-mono font-semibold text-sm">
                      {s.slot_number}
                    </td>
                    <td className="px-2 py-2">
                      <div className="font-medium">{s.product_name || <em className="text-[#C05C47]">(vide)</em>}</div>
                      <div className="text-[9px] text-[#6B6E6B] font-mono">{s.product_sku || "—"}</div>
                    </td>
                    <td className="px-2 py-2 text-center font-mono">{s.capacity_max}</td>
                    <td className={`px-2 py-2 text-center font-mono font-bold bg-[#FBEDEA] text-[#8C3D31] ${isDegraded ? "italic" : ""}`}
                        title={isDegraded ? `Source : ${s.qty_moma_source} (Nayax indispo)` : "Source : Nayax live"}>
                      {s.qty_moma_calc}
                      {isDegraded && <span className="text-[9px] ml-1">⚠</span>}
                    </td>
                    <td className="px-2 py-2 text-center font-mono">
                      {s.qty_am_lots}
                    </td>
                    <td className={`px-2 py-2 text-center font-mono ${deltaColor}`}>
                      {s.delta > 0 ? `+${s.delta}` : s.delta}
                    </td>
                    <td className="px-2 py-2 text-center font-mono">
                      {s.to_recharge > 0 ? (
                        <span className="font-semibold text-[#1F4E3D]">{s.to_recharge}</span>
                      ) : (
                        <span className="text-[#6B6E6B]">0</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => setAuditSlot(s)}
                        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-[#EAF4EE] text-[#1F4E3D] transition-colors"
                        title="Voir le détail de ce slot (baseline, mouvements, Nayax live)"
                        data-testid={`moma-slot-audit-${s.slot_number}`}
                      >
                        <Search className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer légende */}
      <div className="px-4 py-2 border-t border-[#E5E5E0] bg-[#FAFAF7] text-[10px] text-[#6B6E6B] leading-relaxed">
        <strong>Comment ça marche :</strong> Résiduel MOMA = dernier chargement AM (tournée) − ventes MOMA depuis.
        Écart <span className="text-[#C05C47] font-semibold">rouge</span> = AM &gt; MOMA (perte présumée / vol).
        Écart <span className="text-[#8C6B2F] font-semibold">jaune</span> = AM &lt; MOMA (sync ventes en retard / erreur).
        <span className="ml-2">Snapshot auto toutes les 15 min.</span>
      </div>

      {/* Phase 99b — Refill éclair mobile */}
      <QuickRefillDialog
        open={quickRefillOpen}
        onOpenChange={setQuickRefillOpen}
        distributor={report?.distributor}
        onRefilled={() => loadReport()}
      />

      {/* Phase 101dq — Audit slot détaillé (baseline, mouvements, Nayax live, aligner) */}
      <SlotAuditDrawer
        open={!!auditSlot}
        onOpenChange={(v) => { if (!v) setAuditSlot(null); }}
        distributor={report?.distributor}
        slot={auditSlot}
        onAdjusted={() => loadReport()}
      />
    </div>
  );
}

function StatCard({ label, value, color, warn, icon, suffix }) {
  return (
    <div
      className="border p-2"
      style={{
        borderColor: warn ? color : "#E5E5E0",
        background: warn ? `${color}0F` : "white",
      }}
    >
      <div className="text-[9px] uppercase tracking-wider text-[#6B6E6B] flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-lg font-mono font-semibold mt-0.5" style={{ color }}>
        {value ?? 0}{suffix ? ` ${suffix}` : ""}
      </div>
    </div>
  );
}
